/**
 * QueueEmpty — per-segment empty state for the queue tab. Layer-aware: under a
 * Paths/Landers/Offers filter the copy speaks about THAT layer, not the whole
 * flow (so "nothing live" never lies while other layers are live). A search
 * that matches nothing gets its own copy and a way out: telling someone the
 * segment is empty when it is only their term that matched nothing is a lie
 * they then act on.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { SearchX } from 'lucide-react';

import { type QueueLayerFilter, type QueueSegment } from './queue-filters';

/** True empty-segment copy (no layer filter active). */
const EMPTY_SEGMENT: Record<QueueSegment, string> = {
  waiting:
    'Nothing waiting. A path / lander / offer added to the bank waits here until you mark it ready to queue.',
  queued:
    'No queued elements. Mark a waiting element ready (or requeue an eliminated one) to put it in the queue for the next rotation.',
  testing: 'Nothing live on the flow yet.',
  removed: 'No eliminated elements.',
};

const LAYER_NOUN: Record<Exclude<QueueLayerFilter, 'all'>, string> = {
  paths: 'paths',
  landers: 'landers',
  offers: 'offers',
};

const SEGMENT_STATE: Record<QueueSegment, string> = {
  waiting: 'waiting',
  queued: 'queued',
  testing: 'live',
  removed: 'eliminated',
};

export function emptyMessage(
  segment: QueueSegment,
  layer: QueueLayerFilter,
): string {
  if (layer === 'all') return EMPTY_SEGMENT[segment];
  return `No ${SEGMENT_STATE[segment]} ${LAYER_NOUN[layer]} in this view.`;
}

export interface QueueEmptyProps {
  segment: QueueSegment;
  layer: QueueLayerFilter;
  /** Non-empty ⇒ the segment has rows, they just don't match. */
  search?: string;
  onClearSearch?: () => void;
}

const SHELL =
  'flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border bg-muted/20 px-4 py-8 text-center';

const QueueEmpty: FC<QueueEmptyProps> = ({
  segment,
  layer,
  search = '',
  onClearSearch,
}) => {
  const term = search.trim();
  if (!term) {
    return (
      <div className={SHELL}>
        <p className="text-xs text-muted-foreground">
          {emptyMessage(segment, layer)}
        </p>
      </div>
    );
  }
  return (
    <div className={SHELL}>
      <SearchX className="h-5 w-5 text-muted-foreground" aria-hidden />
      <p className="text-xs text-muted-foreground">
        Nothing here matches <span className="font-medium text-foreground">“{term}”</span>.
      </p>
      {onClearSearch ? (
        <button
          type="button"
          onClick={onClearSearch}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline"
        >
          Clear the search
        </button>
      ) : null}
    </div>
  );
};

export default QueueEmpty;
